import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { formatDate, formatTime } from '@app/helpers/formatDateTime.helper';
import { formatUserName } from '@app/helpers/formatName.helper';
import { BadgeView } from '@app/helpers/status.helper';
import type { UserAccount } from '@app/models/account.model';
import type { Project } from '@app/models/project.models';
import type {
  PaginatedData,
  ProjectDataRequest,
} from '@app/models/shared.models';
import { GetLocalizedPipe } from '@app/pipes/get-localized.pipe';
import { ProjectAdminService } from '@app/services/admin/project-admin.service';
import { UserService } from '@app/services/admin/user.service';
import { ToastService } from '@app/services/toast.service';
import { GenericStore } from '@app/stores/generic.store';
import { GenericProjectStore } from '@app/stores/projects/generic-project.store';
import { BadgeCellRendererComponent } from '@app/ui-components/grid-table/badge-cell-renderer/badge-cell-renderer.component';
import { customRendererCellStyle } from '@app/ui-components/grid-table/custom-renderer-cell-style';
import { GridTableComponent } from '@app/ui-components/grid-table/grid-table.component';
import { LinkCellRendererComponent } from '@app/ui-components/grid-table/link-cell-renderer/link-cell-renderer.component';
import { TwoLineCellRendererComponent } from '@app/ui-components/grid-table/two-line-cell-renderer/two-line-cell-renderer.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type {
  ColDef,
  ICellRendererParams,
  IDatasource,
  IGetRowsParams,
} from 'ag-grid-community';
import { catchError, finalize, forkJoin, map, of, switchMap } from 'rxjs';

type ProjectRow = {
  id: string | number;
  submittedOn: string;
  submittedOnDate: string;
  submittedOnTime: string;
  name: string;
  location: string;
  contactUserId?: string | null;
  contactPerson?: string;
  currency: string;
  statusId: number | null;
};

@Component({
  selector: 'app-project-ideas-list',
  standalone: true,
  imports: [CommonModule, GridTableComponent, TranslatePipe],
  templateUrl: './project-ideas-list.component.html',
  styleUrl: './project-ideas-list.component.scss',
  providers: [GetLocalizedPipe],
})
export class ProjectIdeasListComponent implements OnInit {
  protected columns: ColDef<ProjectRow>[] = [];

  protected projectsDataSource: IDatasource = {
    getRows: (params: IGetRowsParams) => this.loadProjects(params),
  };

  protected readonly searchValue = signal<string | undefined>(undefined);
  protected readonly isLoading = signal(true);

  protected readonly currencyFilter = signal<string[] | undefined>(undefined);
  protected readonly countryFilter = signal<string[] | undefined>(undefined);
  protected readonly categoryFilter = signal<string[] | undefined>(undefined);

  private readonly projectService = inject(ProjectAdminService);
  private readonly userService = inject(UserService);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);
  private readonly genericStore = inject(GenericStore);
  private readonly genericProjectStore = inject(GenericProjectStore);
  private readonly localized = inject(GetLocalizedPipe);
  private readonly destroyRef = inject(DestroyRef);
  private readonly contactUserCache = new Map<string, UserAccount>();

  ngOnInit(): void {
    this.buildColumns();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.buildColumns());
  }

  private buildColumns(): void {
    this.columns = [
      {
        field: 'submittedOn',
        headerName: this.translate.instant(
          'admin.projectIdeas.table.submittedOn'
        ),
        cellRenderer: TwoLineCellRendererComponent,
        cellRendererParams: (param: ICellRendererParams<ProjectRow>) => ({
          title: param.data?.submittedOnDate ?? '',
          subtitle: param.data?.submittedOnTime ?? '',
        }),
        cellStyle: customRendererCellStyle,
        width: 120,
        flex: 0,
        suppressNavigable: true,
        editable: false,
        sortable: false,
      },
      {
        field: 'name',
        headerName: this.translate.instant('admin.projectIdeas.table.name'),
        cellRenderer: LinkCellRendererComponent,
        cellRendererParams: {
          context: { baseRoute: '/admin/project-ideas' },
        },
      },
      {
        field: 'location',
        colId: 'country',
        headerName: this.translate.instant('admin.projectIdeas.table.location'),
      },
      {
        field: 'contactPerson',
        headerName: this.translate.instant(
          'admin.projectIdeas.table.contactPerson'
        ),
        sortable: false,
      },
      {
        field: 'currency',
        headerName: this.translate.instant('admin.projectIdeas.table.currency'),
        width: 110,
        flex: 0,
      },
      {
        field: 'statusId',
        colId: 'status',
        headerName: this.translate.instant('admin.projectIdeas.table.status'),
        cellRenderer: BadgeCellRendererComponent,
        cellClass: 'ag-no-ellipsis',
        cellStyle: customRendererCellStyle,
        cellRendererParams: (param: ICellRendererParams<ProjectRow>) => {
          const id = param.data?.statusId ?? null;
          const statuses = this.genericProjectStore.projectStatusEntities();
          const name =
            statuses.find((status) => status.id === id)?.name?.trim() ?? '';
          const labelKey = name ? `generic.projectStatus.${name}` : '-';

          const view: BadgeView = {
            labelKey,
            badgeClass: 'badge-subtle-default',
          };

          return { view };
        },
        minWidth: 220,
        flex: 0,
        sortable: false,
      },
    ];
  }

  private loadProjects(params: IGetRowsParams): void {
    this.isLoading.set(true);
    const skip = params.startRow;
    const take = params.endRow - params.startRow;

    const sortModel = params.sortModel ?? [];
    const sort: ProjectDataRequest['sort'] | undefined =
      sortModel.length > 0
        ? sortModel.map((s) => ({
            selector: s.colId,
            desc: s.sort === 'desc',
          }))
        : undefined;

    const request: ProjectDataRequest = {
      skip,
      take,
      sort,
      filter: this.searchValue() ?? undefined,
      currency: this.currencyFilter() ?? undefined,
      country: this.countryFilter() ?? undefined,
      category: this.categoryFilter() ?? undefined,
    };

    this.projectService
      .getProjectIdeas(request)
      .pipe(
        switchMap((res: PaginatedData<Project>) => {
          const rowCount = res?.rowCount ?? res.data?.length ?? 0;
          const list: Project[] = res?.data ?? [];

          const contactIds = Array.from(
            new Set(
              list
                .map((p) => (p as any).contactUserId as string)
                .filter(Boolean)
            )
          );

          const idsToFetch = contactIds.filter(
            (id) => !this.contactUserCache.has(id)
          );

          if (idsToFetch.length === 0) {
            return of({
              list,
              contacts: this.contactUserCache,
              rowCount,
            });
          }

          const calls = idsToFetch.map((id) =>
            this.userService.getUserById(id).pipe(
              catchError(() => of(null as unknown as UserAccount)),
              map((user) => [id, user] as const)
            )
          );

          return forkJoin(calls).pipe(
            map((pairs) => {
              for (const [id, user] of pairs) {
                if (user) {
                  this.contactUserCache.set(id, user);
                }
              }
              return { list, contacts: this.contactUserCache, rowCount };
            })
          );
        }),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: ({ list, contacts, rowCount }) => {
          const projects: ProjectRow[] = list.map((project) => {
            const submittedRaw =
              (project as any)?.classification?.modificationDate ??
              (project as any)?.classification?.creationDate ??
              '';

            const countryCode = (project.country ?? '').trim();
            const countryEnt = this.genericStore
              .countriesEntities()
              .find((x) => x.code === countryCode);
            const locCountry =
              this.localized.transform(countryEnt?.localized) || countryCode;
            const city = (project.city ?? '').toString().trim();
            const contactId = ((project as any)?.contactUserId ?? '') as string;
            const contactUser = contactId ? contacts.get(contactId) : undefined;

            return {
              id: project.id as string | number,
              submittedOn: submittedRaw,
              submittedOnDate: formatDate(submittedRaw),
              submittedOnTime: formatTime(submittedRaw),
              name: (project.name ?? '').toString().trim(),
              location:
                locCountry && city
                  ? `${locCountry}, ${city}`
                  : locCountry || city || '',
              contactUserId: contactId || null,
              contactPerson: formatUserName(contactUser),
              currency: (project.currency ?? '').toString().trim(),
              statusId: (project.state ?? null) as number | null,
            };
          });

          params.successCallback(projects, rowCount);
        },
        error: () => {
          params.failCallback();
          this.toast.error(
            this.translate.instant('admin.toasts.tableLoadFailed')
          );
        },
      });
  }
}
